import { NgStyle, NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  forwardRef,
  inject,
  Injector,
  Input,
  output,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import type { ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import type { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller/lib/ngx-virtual-scroller.component';
import { deepEqual } from 'fast-equals';
import { Subject, merge } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

import { FooterResolverDirective } from './default-templates/footer/footer-resolver.directive';
import { HeaderResolverDirective } from './default-templates/header/header-resolver.directive';
import { ListItemResolverDirective } from './default-templates/list-item/list-item-resolver.directive';
import { SelectedItemResolverDirective } from './default-templates/selected-item/selected-item-resolver.directive';
import { TriggerResolverDirective } from './default-templates/trigger/trigger-resolver.directive';
import { WithKeyNavDirective } from './features/keyboard-feature.directive';
import { SelectController } from './services/select-controller.service';
import { SelectItemsService } from './services/select-items.service';
import { SelectTemplatesService } from './services/select-templates.service';
import { InlineOpenStrategy } from './strategies/open/inline-open';
import {
  injectOpenStrategy,
  provideOpenStrategy,
  StrategyRelevance,
} from './strategies/open/types';
import type { Select2Item } from './types';
import { SELECT_CONTROLLER } from './types';
import { ItemWrapperComponent } from './utils/item-wrapper.component';
import { DropdownComponent, DropdownWidth } from '../../common/dropdown/dropdown.component';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import type { OverlayPosition } from '../../core/overlay/overlay-directives';

export const defaultCompareItemsFn = <Item extends Select2Item>(
  a: Item,
  b: Item | { value: Item['value'] },
) => deepEqual(a.value, b.value);

const noop = (...args: any[]): void => void 0;
const vsCompareItemsFallback = (a: Select2Item | undefined, b: Select2Item | undefined) => a === b;

@Component({
  selector: 'rt-select2',
  imports: [
    DropdownComponent,
    VirtualScrollerModule,
    NgTemplateOutlet,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    NgStyle,
    ItemWrapperComponent,
    SpinnerComponent,
  ],
  templateUrl: './select2.component.html',
  styleUrls: ['./select2.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // eslint-disable-next-line @angular-eslint/no-forward-ref
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Select2Component), multi: true },
    { provide: SELECT_CONTROLLER, useExisting: SelectController },
    SelectItemsService,
    SelectTemplatesService,
    SelectController,
    provideOpenStrategy(InlineOpenStrategy, StrategyRelevance.Default),
  ],
  hostDirectives: [
    TriggerResolverDirective,
    ListItemResolverDirective,
    SelectedItemResolverDirective,
    HeaderResolverDirective,
    FooterResolverDirective,
    WithKeyNavDirective,
  ],
  host: {
    '[class.disabled]': 'disabled',
  },
})
export class Select2Component<Item extends Select2Item> implements ControlValueAccessor {
  public dropdownConnectedOverlay = viewChild.required<ConnectedOverlayDirective>(
    'dropdownConnectedOverlay',
  );
  public dropdownTemplate = viewChild.required<TemplateRef<void>>('dropdownTemplate');
  public dropdownTemplateVcRef = viewChild.required('dropdownTemplate', { read: ViewContainerRef });
  public triggerOverlay = viewChild.required<ConnectedOverlayDirective>('triggerOverlay');
  public inlineTriggerOrigin = viewChild.required<OverlayOriginDirective>('inlineTriggerOrigin');
  public overlayTriggerOrigin = viewChild<OverlayOriginDirective>('overlayTriggerOrigin');
  public virtualScroll = viewChild<VirtualScrollerComponent>('virtual');

  @Input() public items: Item[];
  @Input() public overlayWidth: number | string;
  @Input() public overlayMinWidth: number | string;
  @Input() public overlayPosition: OverlayPosition[];
  @Input() public hiddenItems: Item[] | null;
  @Input() public compareItemsFn = defaultCompareItemsFn;
  @Input() public autoClose: boolean = true;
  @Input() public itemsLoading: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public vsCompareItems: (a: Item | undefined, b: Item | undefined) => boolean;

  public vsUpdate = output<IPageInfo>();
  public vsEnd = output<IPageInfo>();
  public selection = output<any>();
  public close = output<void>();
  public clear = output<void>();
  public isOpen = output<boolean>();
  public uiItemSelect = outputFromObservable(<Subject<Item>>this.selectController.select$);

  public virtualScrollStyle: Partial<CSSStyleDeclaration>;
  public virtualScrollIndexOffset: number = 0;
  public DropdownWidth = DropdownWidth;
  public vsCompareItemsFallback = vsCompareItemsFallback;
  public onTouched = noop;

  protected openStrategy = injectOpenStrategy();

  private ngOnDestroy$ = new Subject<void>();
  private onChange = noop;
  private injector = inject(Injector);

  constructor(
    public selectItemsService: SelectItemsService,
    public selectTemplatesService: SelectTemplatesService,
    public selectController: SelectController,
    private cdRef: ChangeDetectorRef,
  ) {
    this.handleVirtualScrollTransition();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.compareItemsFn) {
      this.selectController.itemSelector.setCompareItemsFn(
        this.compareItemsFn ?? defaultCompareItemsFn,
      );
    }

    if (changes.hiddenItems) {
      this.selectController.itemSelector.setHiddenItems(this.hiddenItems);
    }

    if (changes.items && this.items) {
      this.selectItemsService.setItems(this.items);
    }

    if (
      changes.overlayWidth ||
      changes.overlayMinWidth ||
      changes.overlayPosition ||
      changes.autoClose
    ) {
      this.openStrategy.setOptions({
        overlayWidth: this.overlayWidth,
        overlayMinWidth: this.overlayMinWidth,
        overlayPosition: this.overlayPosition,
        autoClose: this.autoClose,
      });
    }
  }

  public ngOnInit() {
    this.openStrategy.register({
      inlineTriggerOrigin: this.inlineTriggerOrigin(),
      dropdownConnectedOverlay: this.dropdownConnectedOverlay(),
      dropdownTemplate: this.dropdownTemplate(),
      dropdownTemplateVcRef: this.dropdownTemplateVcRef(),
      triggerOverlay: this.triggerOverlay(),
      overlayTriggerOrigin$: toObservable(this.overlayTriggerOrigin, { injector: this.injector }),
    });

    this.openStrategy.init();
    toObservable(this.selectController.isOpen, { injector: this.injector })
      .pipe(skip(1), takeUntil(this.ngOnDestroy$))
      .subscribe(isOpen => {
        this.isOpen.emit(isOpen);
        if (isOpen) return;

        this.close.emit();
      });
  }

  public ngAfterContentInit() {
    this.selectTemplatesService.resolveTemplates();
    this.handleClear();
    this.handleSelect();

    merge(
      this.selectController.navigation$,
      this.selectController.detectChanges$,
      toObservable(this.selectController.itemSelector.selected, { injector: this.injector }),
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public onKeyDown(event: KeyboardEvent) {
    this.selectController.keyDown$.next(event);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onVirtualScrollUpdate(event: IPageInfo) {
    this.virtualScrollIndexOffset = event.startIndexWithBuffer;
    this.vsUpdate.emit(event);
    this.selectController.detectChanges$.next();
  }

  public onVsEnd(event: IPageInfo) {
    this.vsEnd.emit(event);
  }

  public registerOnChange(fn: (v: Select2Item | Select2Item[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.selectController.setDisabled(isDisabled);
  }

  public writeValue(value: Item['value'] | Array<Item['value']>): void {
    this.selectController.itemSelector.writeValue(value);
    this.selectController.detectChanges$.next();
  }

  public scrollTop() {
    this.virtualScroll()?.scrollToPosition(0, 0);
  }

  private handleClear() {
    this.selectController.clear$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      const { itemSelector } = this.selectController;
      itemSelector.clear();
      this.onChange(itemSelector.getValue());
      this.clear.emit();
      this.selectController.detectChanges$.next();
    });
  }

  private handleSelect() {
    const { itemSelector, detectChanges$, select$ } = this.selectController;

    itemSelector.selectionChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(selected => {
      const value = itemSelector.getValue();
      this.onChange(value);
      itemSelector.writeValue(value);
      this.selection.emit(selected);
      detectChanges$.next();
    });

    select$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(item => {
      itemSelector.select(<Select2Item>item);
      detectChanges$.next();
    });
  }

  private handleVirtualScrollTransition() {
    effect(
      () => {
        const virtualScroll = this.virtualScroll();
        this.selectController.virtualScroll.set(virtualScroll);
        if (!virtualScroll) return;

        const scrollElement: HTMLElement = virtualScroll['contentElementRef'].nativeElement;
        if (this.selectItemsService.itemsToRender().length > 20) {
          scrollElement.style.position = 'absolute';
          this.virtualScrollStyle = { height: '270px' };
        } else {
          scrollElement.style.position = 'static';
          this.virtualScrollStyle = { maxHeight: '270px' };
        }
        this.selectController.detectChanges$.next();
      },
      { allowSignalWrites: true },
    );
  }
}
